import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cascadePhase,
  getProject,
  patchPhase,
  type Phase,
} from '../lib/api';

/** Structural minimum the modal needs from a phase row — both `Phase`
 *  (single GET) and `PhaseSummary` (project list response) satisfy this,
 *  so the same modal works whether opened from PhasePage or ProjectPage. */
export type PhaseForDateEdit = Pick<
  Phase,
  'id' | 'phase_number' | 'phase_name' | 'target_start_date' | 'target_end_date'
>;

/**
 * PhaseDateEditModal — edit a phase's target dates and optionally cascade
 * the delta to downstream phases (D-035 backend, D.5 UI).
 *
 * Per ARCH §4.5, cascade is **prompt-then-action**:
 *   1. User changes `target_end_date` (and/or `target_start_date`).
 *   2. On Save, if `target_end_date` changed AND there are downstream
 *      phases AND the delta is non-zero → show inline cascade prompt with
 *      two buttons:
 *        • "Shift downstream by N days" — PATCH the phase + POST cascade
 *        • "This phase only"            — PATCH the phase only
 *   3. If `target_end_date` didn't change OR no downstream phases exist,
 *      Save just PATCHes without prompting.
 *
 * The modal stays open during the cascade-prompt step so the user can see
 * what they changed AND make the cascade decision in a single context. No
 * second modal pop-up; no "save then ask" sequence that loses focus.
 */
export function PhaseDateEditModal({
  open,
  onOpenChange,
  phase,
  projectId,
  downstreamPhaseCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phase: PhaseForDateEdit;
  projectId: string;
  downstreamPhaseCount: number;
}) {
  const qc = useQueryClient();

  const [targetStart, setTargetStart] = useState('');
  const [targetEnd, setTargetEnd] = useState('');
  // Cascade-prompt phase of the modal flow: 'editing' | 'choose-cascade'
  const [step, setStep] = useState<'editing' | 'choose-cascade'>('editing');

  useEffect(() => {
    if (!open) return;
    setTargetStart(phase.target_start_date?.slice(0, 10) ?? '');
    setTargetEnd(phase.target_end_date?.slice(0, 10) ?? '');
    setStep('editing');
  }, [open, phase]);

  const originalEnd = phase.target_end_date?.slice(0, 10) ?? '';
  const deltaDays = useMemo(
    () => daysBetween(originalEnd, targetEnd),
    [originalEnd, targetEnd]
  );

  const saveMut = useMutation({
    mutationFn: async (opts: { cascade: boolean }) => {
      // 1. PATCH the phase's own dates
      await patchPhase(phase.id, {
        target_start_date: targetStart || null,
        target_end_date: targetEnd || null,
      });
      // 2. Optionally POST cascade
      if (opts.cascade && deltaDays !== 0) {
        await cascadePhase(phase.id, deltaDays);
      }
      // Invalidate everything that reads phase dates
      await qc.invalidateQueries({ queryKey: ['phases'] });
      await qc.invalidateQueries({ queryKey: ['projects', 'one', projectId] });
    },
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  function onSaveClick() {
    // If the end date changed AND there are downstream phases, prompt for
    // cascade. Otherwise just save.
    if (
      deltaDays !== 0 &&
      downstreamPhaseCount > 0 &&
      originalEnd && targetEnd
    ) {
      setStep('choose-cascade');
      return;
    }
    saveMut.mutate({ cascade: false });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[60]"
          style={{ background: 'rgba(28, 38, 56, 0.55)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2 w-[min(540px,95vw)] bg-surface border border-border rounded-lg shadow-2xl"
        >
          <header className="px-5 py-3 border-b border-border flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold">
              Edit phase dates
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-ink-3 hover:text-ink text-sm px-2 py-0.5 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Close"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="p-5">
            {step === 'editing' && (
              <>
                <p className="text-sm text-ink-2 mb-3 num">
                  Phase {String(phase.phase_number).padStart(2, '0')} —{' '}
                  <span className="font-medium text-ink">
                    {phase.phase_name}
                  </span>
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Target start">
                    <input
                      type="date"
                      value={targetStart}
                      onChange={(e) => setTargetStart(e.target.value)}
                      className="w-full border border-border rounded px-2 py-1.5 text-base num bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </Field>
                  <Field label="Target end">
                    <input
                      type="date"
                      value={targetEnd}
                      onChange={(e) => setTargetEnd(e.target.value)}
                      className="w-full border border-border rounded px-2 py-1.5 text-base num bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </Field>
                </div>

                {deltaDays !== 0 && originalEnd && targetEnd && (
                  <p className="mt-3 text-xs num text-ink-2">
                    Target end {deltaSentence(originalEnd, targetEnd, deltaDays)}
                    {downstreamPhaseCount > 0 && (
                      <>
                        {' '}
                        · {downstreamPhaseCount} downstream phase
                        {downstreamPhaseCount === 1 ? '' : 's'} could be
                        shifted by the same amount.
                      </>
                    )}
                  </p>
                )}
              </>
            )}

            {step === 'choose-cascade' && (
              <>
                <p className="text-sm text-ink mb-2">
                  <span className="font-medium">
                    Phase {String(phase.phase_number).padStart(2, '0')} —{' '}
                    {phase.phase_name}
                  </span>{' '}
                  target end {deltaSentence(originalEnd, targetEnd, deltaDays)}.
                </p>
                <p className="text-sm text-ink-2 mb-4">
                  Shift the {downstreamPhaseCount} downstream phase
                  {downstreamPhaseCount === 1 ? '' : 's'} by{' '}
                  <span className="num font-medium text-ink">
                    {Math.abs(deltaDays)} day{Math.abs(deltaDays) === 1 ? '' : 's'}{' '}
                    {deltaDays > 0 ? 'forward' : 'backward'}
                  </span>{' '}
                  too?
                </p>
              </>
            )}

            {saveMut.isError && (
              <p
                className="mt-2 text-xs num"
                style={{ color: 'var(--color-danger)' }}
              >
                {(saveMut.error as Error).message}
              </p>
            )}
          </div>

          <footer className="px-5 py-3 border-t border-border bg-surface-2 flex items-center justify-end gap-2">
            {step === 'editing' && (
              <>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded text-sm border border-border hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={onSaveClick}
                  disabled={saveMut.isPending}
                  className="px-3 py-1.5 rounded text-sm font-medium text-surface focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                  style={{ background: 'var(--color-accent)' }}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
            {step === 'choose-cascade' && (
              <>
                <button
                  type="button"
                  onClick={() => setStep('editing')}
                  className="px-3 py-1.5 rounded text-sm border border-border hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => saveMut.mutate({ cascade: false })}
                  disabled={saveMut.isPending}
                  className="px-3 py-1.5 rounded text-sm border border-border hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                >
                  This phase only
                </button>
                <button
                  type="button"
                  onClick={() => saveMut.mutate({ cascade: true })}
                  disabled={saveMut.isPending}
                  className="px-3 py-1.5 rounded text-sm font-medium text-surface focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                  style={{ background: 'var(--color-accent)' }}
                >
                  {saveMut.isPending
                    ? 'Saving…'
                    : `Shift downstream by ${deltaDays > 0 ? '+' : ''}${deltaDays}d`}
                </button>
              </>
            )}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col text-xs text-ink-2">
      <span className="mb-1 font-semibold uppercase tracking-wide">
        {label}
      </span>
      {children}
    </label>
  );
}

/** Days between two ISO date strings (YYYY-MM-DD). Positive → b is later. */
function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const dayMs = 86400000;
  const t0 = new Date(a + 'T00:00:00Z').getTime();
  const t1 = new Date(b + 'T00:00:00Z').getTime();
  return Math.round((t1 - t0) / dayMs);
}

function deltaSentence(from: string, to: string, delta: number): string {
  const sign = delta > 0 ? '+' : '';
  const word = delta === 1 ? 'day' : 'days';
  return `moved from ${from} to ${to} (${sign}${delta} ${word})`;
}

// ---------------------------------------------------------------------------
// Hook for parent components — fetches the project once to know how many
// downstream phases exist (needed to decide whether to prompt cascade).
// ---------------------------------------------------------------------------

export function useDownstreamPhaseCount(
  projectId: string,
  phaseNumber: number
): number {
  // The caller may pass an empty projectId on initial render before the
  // phase data has loaded — `enabled: !!projectId` skips the fetch in
  // that case so we don't fire a request for `/api/projects/`.
  const projectQ = useQuery({
    queryKey: ['projects', 'one', projectId],
    queryFn: () => getProject(projectId),
    enabled: !!projectId,
  });
  if (!projectQ.data) return 0;
  return projectQ.data.phases.filter((p) => p.phase_number > phaseNumber)
    .length;
}
